import type { SxProps, Theme } from '@mui/material'
import type { SystemStyleObject } from '@mui/system'
import type {
  ContentContainerConfig,
  NodeWrapperConfig,
  TreeNodeStyleConfig,
} from '../types/SessionTreeProps'

export const DEFAULT_TOGGLE_WIDTH = '1.5rem'
export const DEFAULT_VERTICAL_GAP = 2.5
export const DEFAULT_FONT_FAMILY = 'inherit'
export const DEFAULT_BORDER_STYLE = 'solid'
export const DEFAULT_BORDER_WIDTH = '1px'
export const DEFAULT_BORDER_COLOR = 'divider'
export const DEFAULT_BORDER_RADIUS = 1

export const DEFAULT_HOST_FONT_SIZE = '1.35rem'
export const DEFAULT_HOST_FONT_WEIGHT = 500
export const DEFAULT_PROJECT_FONT_SIZE = '1.15rem'
export const DEFAULT_PROJECT_FONT_WEIGHT = 500
export const DEFAULT_SESSION_FONT_SIZE = '1rem'
export const DEFAULT_SESSION_FONT_WEIGHT = 400

export const DEFAULT_TOGGLE_HEIGHT = '1.5rem'

export function createNodeHeaderSx(
  config?: TreeNodeStyleConfig,
  defaultFontSize?: string,
  defaultFontWeight?: number,
): SxProps<Theme> {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    flex: 1,
    cursor: 'pointer',
    userSelect: 'none',
    fontFamily: config?.fontFamily ?? DEFAULT_FONT_FAMILY,
    fontSize: config?.fontSize ?? defaultFontSize ?? DEFAULT_HOST_FONT_SIZE,
    fontWeight: config?.fontWeight ?? defaultFontWeight ?? DEFAULT_HOST_FONT_WEIGHT,
    color: config?.color ?? 'text.primary',
  }
}

export const DEFAULT_TOGGLE_FONT_SIZE = '1.25rem'

export function createToggleSx(config?: TreeNodeStyleConfig): SxProps<Theme> {
  const toggleConfig = config?.toggleIcon

  return {
    width: toggleConfig?.size ?? DEFAULT_TOGGLE_WIDTH,
    minWidth: toggleConfig?.size ?? DEFAULT_TOGGLE_WIDTH,
    height: toggleConfig?.size ?? DEFAULT_TOGGLE_HEIGHT,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: toggleConfig?.color ?? 'text.secondary',
    fontFamily: 'monospace',
    fontSize: toggleConfig?.size ?? DEFAULT_TOGGLE_FONT_SIZE,
    lineHeight: 1,
    opacity: 0,
    visibility: 'hidden',
    transition: 'opacity 150ms ease-in, visibility 0ms 150ms',
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
    },
    '@media (hover: none)': {
      opacity: 1,
      visibility: 'visible',
    },
  }
}

export function createNodeIconSx(config?: TreeNodeStyleConfig): SxProps<Theme> {
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: DEFAULT_TOGGLE_WIDTH,
    height: DEFAULT_TOGGLE_HEIGHT,
    fontSize: config?.iconSize ?? '1.25rem',
    color: config?.iconColor ?? 'text.secondary',
    marginRight: config?.iconGap ?? 0.5,
  }
}

export const labelSx: SxProps<Theme> = {
  flex: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

export function createCountSx(config?: TreeNodeStyleConfig): SxProps<Theme> {
  return {
    display: 'inline',
    color: config?.countColor ?? 'text.disabled',
    fontSize: 'inherit',
    marginLeft: 0.5,
    fontWeight: 400,
  }
}

export const actionsSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  marginLeft: 'auto',
  opacity: 0,
  visibility: 'hidden',
  transition: 'opacity 150ms ease-in, visibility 0ms 150ms',
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
  },
  '@media (hover: none)': {
    opacity: 1,
    visibility: 'visible',
  },
}

export const nodeRowHoverRevealSx: SystemStyleObject<Theme> = {
  '&:hover .actions-container': {
    opacity: 1,
    visibility: 'visible',
    transition: 'opacity 150ms ease-in, visibility 0ms',
  },
  '&:focus-within .actions-container': {
    opacity: 1,
    visibility: 'visible',
    transition: 'opacity 150ms ease-in, visibility 0ms',
  },
  '&:hover .node-toggle': {
    opacity: 1,
    visibility: 'visible',
    transition: 'opacity 150ms ease-in, visibility 0ms',
  },
  '&:focus-within .node-toggle': {
    opacity: 1,
    visibility: 'visible',
    transition: 'opacity 150ms ease-in, visibility 0ms',
  },
  '@media (prefers-reduced-motion: reduce)': {
    '&:hover .actions-container, &:focus-within .actions-container, &:hover .node-toggle, &:focus-within .node-toggle':
      {
        transition: 'none',
      },
  },
}

export const DEFAULT_CONTENT_CONTAINER_MARGIN_LEFT = `calc(${DEFAULT_TOGGLE_WIDTH} * 4 + 16px)`
export const DEFAULT_CONTENT_CONTAINER_MARGIN_TOP = 0.5

export function createContentContainerSx(config?: ContentContainerConfig): SxProps<Theme> {
  return {
    borderWidth: config?.borderWidth ?? DEFAULT_BORDER_WIDTH,
    borderColor: config?.borderColor ?? DEFAULT_BORDER_COLOR,
    borderStyle: config?.borderStyle ?? DEFAULT_BORDER_STYLE,
    borderRadius: config?.borderRadius ?? DEFAULT_BORDER_RADIUS,
    padding: config?.padding ?? 1.5,
    backgroundColor: config?.backgroundColor ?? 'transparent',
    marginTop: config?.marginTop ?? DEFAULT_CONTENT_CONTAINER_MARGIN_TOP,
    marginLeft: config?.marginLeft ?? DEFAULT_CONTENT_CONTAINER_MARGIN_LEFT,
  }
}

export const childrenContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
}

export const DEFAULT_EXPANDED_ICON = '▾'
export const DEFAULT_COLLAPSED_ICON = '▸'

export function createWrapperSx(config?: NodeWrapperConfig): SxProps<Theme> | null {
  if (!config) return null

  return {
    backgroundColor: config.backgroundColor ?? 'transparent',
    borderWidth: config.borderWidth ?? 0,
    borderColor: config.borderColor ?? 'transparent',
    borderStyle: config.borderStyle ?? 'none',
    borderRadius: config.borderRadius ?? 0,
    padding: config.padding ?? 0,
    marginBottom: config.marginBottom ?? 0,
    ...(config.boxShadow && { boxShadow: config.boxShadow }),
  }
}
